// @ts-check
import { NodeTranslator } from '@translator';
import { translateChildNodes } from '../../../../v2/exporter/helpers/index.js';
import { cloneMark, cloneXmlNode, applyRunPropertiesTemplate, resolveFontFamily } from './helpers/helpers.js';
import { ensureTrackedWrapper, prepareRunTrackingContext } from './helpers/track-change-helpers.js';
import { appendTrackFormatChangeToRunProperties, findTrackFormatMark } from '../../helpers.js';
import { translator as wHyperlinkTranslator } from '../hyperlink/hyperlink-translator.js';
import { translator as wRPrTranslator } from '../rpr';
import validXmlAttributes from './attributes/index.js';
import { handleStyleChangeMarksV2 } from '../../../../v2/importer/markImporter.js';
import { getParagraphStyleRunPropertiesFromStylesXml } from '@converter/export-helpers/run-properties-export.js';
import { encodeMarksFromRPr, resolveRunProperties } from '../../../../styles.js';
/** @type {import('@translator').XmlNodeName} */
const XML_NODE_NAME = 'w:r';

/**
 * Represent OOXML <w:r> as a SuperDoc inline node named 'run'.
 * Content within the run is preserved as node children with applied marks.
 */
/** @type {import('@translator').SuperDocNodeOrKeyName} */
const SD_KEY_NAME = 'run';

const REFERENCE_RUN_STYLE_BY_XML_NAME = {
  'w:footnoteReference': 'FootnoteReference',
  'w:endnoteReference': 'EndnoteReference',
};

const hasXmlNodeNamed = (node, targetName) => {
  if (!node || typeof node !== 'object') return false;
  if (node.name === targetName) return true;
  if (!Array.isArray(node.elements)) return false;
  return node.elements.some((child) => hasXmlNodeNamed(child, targetName));
};

const COMPLEX_SCRIPT_CODEPOINT_RANGES = [
  [0x0590, 0x08ff],
  [0x0900, 0x109f],
  [0x1780, 0x18af],
  [0x1cd0, 0x1cff],
  [0xa800, 0xa8ff],
  [0xaa60, 0xaa7f],
];

const textHasComplexScript = (text) => {
  for (const char of text) {
    const codePoint = char.codePointAt(0);
    if (COMPLEX_SCRIPT_CODEPOINT_RANGES.some(([start, end]) => codePoint >= start && codePoint <= end)) {
      return true;
    }
  }
  return false;
};

const hasComplexScriptText = (node) => {
  if (!node || typeof node !== 'object') return false;
  if (typeof node.text === 'string' && textHasComplexScript(node.text)) return true;
  if (!Array.isArray(node.content)) return false;
  return node.content.some((child) => hasComplexScriptText(child));
};

const getRunPropertiesNode = (runNode) => {
  if (!runNode) return null;
  if (!Array.isArray(runNode.elements)) runNode.elements = [];

  let runPropertiesNode = runNode.elements.find((element) => element?.name === 'w:rPr');
  if (!runPropertiesNode) {
    runPropertiesNode = { type: 'element', name: 'w:rPr', elements: [] };
    runNode.elements.unshift(runPropertiesNode);
  }

  if (!Array.isArray(runPropertiesNode.elements)) {
    runPropertiesNode.elements = [];
  }

  return runPropertiesNode;
};

const collectRunPropertyChanges = (runNode) => {
  const runPropertiesNode = runNode?.elements?.find((element) => element?.name === 'w:rPr');
  if (!Array.isArray(runPropertiesNode?.elements)) return [];

  return runPropertiesNode.elements
    .filter((element) => element?.name === 'w:rPrChange')
    .map((element) => cloneXmlNode(element));
};

const restoreRunPropertyChanges = (runNode, runPropertyChanges = []) => {
  if (!runPropertyChanges.length) return;

  const runPropertiesNode = getRunPropertiesNode(runNode);
  const existingNames = new Set(runPropertiesNode.elements.map((element) => element?.name));

  runPropertyChanges.forEach((changeElement) => {
    if (!changeElement?.name || existingNames.has(changeElement.name)) return;
    runPropertiesNode.elements.push(changeElement);
    existingNames.add(changeElement.name);
  });
};

const ensureReferenceRunFormatting = (runNode, referenceXmlName) => {
  const styleId = REFERENCE_RUN_STYLE_BY_XML_NAME[referenceXmlName];
  if (!styleId) return;

  if (!Array.isArray(runNode.elements)) runNode.elements = [];
  let runProps = runNode.elements.find((el) => el?.name === 'w:rPr');
  if (!runProps) {
    runProps = { name: 'w:rPr', elements: [] };
    runNode.elements.unshift(runProps);
  }

  if (!Array.isArray(runProps.elements)) runProps.elements = [];

  const hasStyle = runProps.elements.some((el) => el?.name === 'w:rStyle');
  if (!hasStyle) {
    runProps.elements.push({ name: 'w:rStyle', attributes: { 'w:val': styleId } });
  }

  const hasVertAlign = runProps.elements.some((el) => el?.name === 'w:vertAlign');
  if (!hasVertAlign) {
    runProps.elements.push({ name: 'w:vertAlign', attributes: { 'w:val': 'superscript' } });
  }
};

/*
 * Wraps the provided content in a SuperDoc run node.
 * runProperties = resolved (from combine). runPropertiesInlineKeys = keys marked inline at combine (export only these).
 * runPropertiesStyleKeys = keys from the run's style in styles.xml (export omits these).
 * runPropertiesOverrideKeys = keys that override the style (inline ∩ style); export includes these to preserve user overrides.
 */
const createRunNodeWithContent = (
  content,
  encodedAttrs,
  runLevelMarks,
  resolvedRunProperties,
  inlineKeysFromCombine,
  runPropertiesStyleKeys = null,
  runPropertiesOverrideKeys = null,
) => {
  const node = {
    type: SD_KEY_NAME,
    content,
    attrs: {
      ...encodedAttrs,
      runProperties: resolvedRunProperties,
      runPropertiesInlineKeys: inlineKeysFromCombine,
      runPropertiesStyleKeys: runPropertiesStyleKeys?.length ? runPropertiesStyleKeys : null,
      runPropertiesOverrideKeys: runPropertiesOverrideKeys?.length ? runPropertiesOverrideKeys : null,
    },
  };
  if (runLevelMarks.length) {
    node.marks = runLevelMarks.map((mark) => cloneMark(mark));
  }
  return node;
};

const encode = (params, encodedAttrs = {}) => {
  const { nodes = [], nodeListHandler } = params || {};
  const runNode = nodes[0];
  if (!runNode) return undefined;

  const elements = Array.isArray(runNode.elements) ? runNode.elements : [];

  // Inline export allow-list = keys from encoded w:rPr only. Do not add keys that appear only
  // after resolveRunProperties (e.g. resolved lang): baseline comparison still inflated document.xml
  // on real documents. Plan-engine / explicit w:rPr continue to seed keys where needed.
  const rPrNode = elements.find((child) => child?.name === 'w:rPr');
  const encodedRunProperties = (rPrNode ? wRPrTranslator.encode({ ...params, nodes: [rPrNode] }) : undefined) ?? {};
  const runPropertiesInlineKeysFromCombine = Object.keys(encodedRunProperties);

  // Resolving run properties following style hierarchy
  const paragraphProperties = params?.extraParams?.paragraphProperties || {};
  let tableInfo = null;
  if (
    params?.extraParams?.rowIndex != null &&
    params?.extraParams?.columnIndex != null &&
    params?.extraParams?.tableProperties != null &&
    params?.extraParams?.totalColumns != null &&
    params?.extraParams?.totalRows != null
  ) {
    tableInfo = {
      rowIndex: params.extraParams.rowIndex,
      cellIndex: params.extraParams.columnIndex,
      tableProperties: params.extraParams.tableProperties,
      numCells: params.extraParams.totalColumns,
      numRows: params.extraParams.totalRows,
    };
  }
  const resolvedRunProperties = resolveRunProperties(
    params,
    encodedRunProperties,
    paragraphProperties,
    tableInfo,
    false,
    params?.extraParams?.numberingDefinedInline,
  );

  // Parsing marks from run properties
  const marksResult = encodeMarksFromRPr(resolvedRunProperties, params?.docx);
  const marks = Array.isArray(marksResult) ? marksResult : [];
  const rPrChange = rPrNode?.elements?.find((el) => el.name === 'w:rPrChange');
  const styleChangeMarks = handleStyleChangeMarksV2(rPrChange, marks, params) || [];

  // Handling direct marks on the run node
  let runLevelMarks = Array.isArray(runNode.marks) ? runNode.marks.map((mark) => cloneMark(mark)) : [];
  if (styleChangeMarks?.length) {
    runLevelMarks = [...runLevelMarks, ...styleChangeMarks.map((mark) => cloneMark(mark))];
  }

  // Encoding child nodes within the run
  const contentElements = rPrNode ? elements.filter((el) => el !== rPrNode) : elements;
  const childParams = { ...params, nodes: contentElements };
  const content = nodeListHandler?.handler(childParams) || [];

  // Applying marks to child nodes
  const contentWithRunMarks = (Array.isArray(content) ? content : []).map((child) => {
    if (!child || typeof child !== 'object') return child;

    if (child.type === 'passthroughInline') {
      return { ...child, marks: [] };
    }

    // Preserve existing marks on child nodes
    const baseMarks = Array.isArray(child.marks) ? child.marks : [];

    let childMarks = [...marks, ...baseMarks, ...runLevelMarks].map((mark) => cloneMark(mark));

    // De-duplicate marks by type, preserving order (later marks override earlier ones)
    const seenTypes = new Set();
    let textStyleMark;
    childMarks = childMarks.filter((mark) => {
      if (!mark || !mark.type) return false;
      if (seenTypes.has(mark.type)) {
        if (mark.type === 'textStyle') {
          // Merge textStyle attributes
          textStyleMark.attrs = { ...(textStyleMark.attrs || {}), ...(mark.attrs || {}) };
          textStyleMark.attrs = resolveFontFamily(textStyleMark.attrs, child?.text);
        }
        return false;
      }
      if (mark.type === 'textStyle') {
        textStyleMark = mark;
      }
      seenTypes.add(mark.type);
      return true;
    });

    // Apply marks to child nodes
    return { ...child, marks: childMarks };
  });

  const filtered = contentWithRunMarks.filter(Boolean);

  // Keys from the run's style (styleId) in styles.xml — don't export these (already in styles.xml)
  let runPropertiesStyleKeys = null;
  if (encodedRunProperties?.styleId && params?.docx) {
    const styleRPr = getParagraphStyleRunPropertiesFromStylesXml(params.docx, encodedRunProperties.styleId, params);
    if (styleRPr && Object.keys(styleRPr).length > 0) {
      runPropertiesStyleKeys = Object.keys(styleRPr);
    }
  }
  // Keys that were in w:rPr and also in the style = explicit overrides; preserve on export
  const runPropertiesOverrideKeys =
    runPropertiesStyleKeys?.length && runPropertiesInlineKeysFromCombine?.length
      ? runPropertiesInlineKeysFromCombine.filter((k) => runPropertiesStyleKeys.includes(k))
      : null;

  const containsBreakNodes = filtered.some((child) => child?.type === 'lineBreak');
  if (!containsBreakNodes) {
    const defaultNode = createRunNodeWithContent(
      filtered,
      encodedAttrs,
      runLevelMarks,
      resolvedRunProperties,
      runPropertiesInlineKeysFromCombine,
      runPropertiesStyleKeys,
      runPropertiesOverrideKeys,
    );
    return defaultNode;
  }

  const splitRuns = [];
  let currentChunk = [];
  /**
   * OOXML sometimes bundles multiple <w:t> siblings and <w:br/> tags inside one <w:r>.
   * Our renderer expects each break to be wrapped in its own run, so we finalize
   * the accumulated text chunk before emitting a break run.
   */
  const finalizeTextChunk = () => {
    if (!currentChunk.length) return;
    const chunkNode = createRunNodeWithContent(
      currentChunk,
      encodedAttrs,
      runLevelMarks,
      resolvedRunProperties,
      runPropertiesInlineKeysFromCombine,
      runPropertiesStyleKeys,
      runPropertiesOverrideKeys,
    );
    if (chunkNode) splitRuns.push(chunkNode);
    currentChunk = [];
  };

  filtered.forEach((child) => {
    if (child?.type === 'lineBreak') {
      finalizeTextChunk();
      const breakNode = createRunNodeWithContent(
        [child],
        encodedAttrs,
        runLevelMarks,
        resolvedRunProperties,
        runPropertiesInlineKeysFromCombine,
        runPropertiesStyleKeys,
        runPropertiesOverrideKeys,
      );
      if (breakNode) splitRuns.push(breakNode);
    } else {
      currentChunk.push(child);
    }
  });
  finalizeTextChunk();

  return splitRuns;
};

const decode = (params, decodedAttrs = {}) => {
  const { node } = params || {};
  if (!node) return undefined;

  // Separate links from regular text
  const isLinkNode = node.marks?.some((m) => m.type === 'link');
  if (isLinkNode) {
    const extraParams = {
      ...params.extraParams,
      linkProcessed: true,
    };
    return wHyperlinkTranslator.decode({ ...params, extraParams });
  }

  // Separate out tracking marks
  const { runNode: runNodeForExport, trackingMarksByType } = prepareRunTrackingContext(node);
  const runTrackFormatMark = findTrackFormatMark(runNodeForExport.marks);

  const runAttrs = runNodeForExport.attrs || {};
  const rawRunProperties = runAttrs.runProperties || {};
  // Backward compatibility: older payloads used iCs instead of italicCs for w:iCs.
  // Normalize to a local copy to avoid mutating node attrs during export.
  const runProperties =
    rawRunProperties?.italicCs == null && rawRunProperties?.iCs != null
      ? { ...rawRunProperties, italicCs: rawRunProperties.iCs }
      : rawRunProperties;
  const inlineKeys = runAttrs.runPropertiesInlineKeys;
  const styleKeys = runAttrs.runPropertiesStyleKeys;
  const overrideKeys = runAttrs.runPropertiesOverrideKeys;

  // Export run properties that were inline or that override the style (so user overrides are preserved).
  // Exclude keys that are style-only (in styleKeys but not in overrideKeys).
  // Old collaboration payloads often omit runPropertiesInlineKeys — fall back to all keys on runProperties
  // so those documents still round-trip formatting (accepts larger document.xml vs strict allow-list only).
  const candidateKeys =
    inlineKeys != null ? [...new Set([...(inlineKeys || []), ...(overrideKeys || [])])] : Object.keys(runProperties);
  if (candidateKeys.includes('iCs') && !candidateKeys.includes('italicCs')) {
    candidateKeys.push('italicCs');
  }
  // AIDEV-NOTE: Do NOT force-push 'rtl' into candidateKeys here. Per ECMA Annex I,
  // run rtl participates in the style cascade - flattening it inline on every export
  // breaks style-inherited rtl (e.g. <w:rStyle w:val="RtlChar"/> with rtl in the style).

  const shouldExport = (key) =>
    key in (runProperties || {}) &&
    (!(Array.isArray(styleKeys) && styleKeys.includes(key)) ||
      (Array.isArray(overrideKeys) && overrideKeys.includes(key)));

  const exportKeys = candidateKeys.filter(shouldExport);

  const runPropertiesToExport =
    exportKeys.length > 0 ? Object.fromEntries(exportKeys.map((k) => [k, runProperties[k]])) : {};
  if (hasComplexScriptText(runNodeForExport)) {
    if ('bold' in runPropertiesToExport && !('boldCs' in runPropertiesToExport)) {
      runPropertiesToExport.boldCs = runPropertiesToExport.bold;
    }
    if ('italic' in runPropertiesToExport && !('italicCs' in runPropertiesToExport)) {
      runPropertiesToExport.italicCs = runPropertiesToExport.italic;
    }
  }

  // Decode child nodes within the run
  const exportParams = {
    ...params,
    node: runNodeForExport,
    extraParams: { ...params?.extraParams, runProperties: runProperties },
  };
  if (!exportParams.editor) {
    exportParams.editor = { extensionService: { extensions: [] } };
  }
  const childElements = translateChildNodes(exportParams) || [];

  // Only emit w:rPr when we have inline overrides; omit when empty so we don't write empty or inherited-only rPr.
  let runPropertiesElement =
    Object.keys(runPropertiesToExport).length > 0
      ? wRPrTranslator.decode({
          ...params,
          node: { attrs: { runProperties: runPropertiesToExport } },
        })
      : null;

  const runPropsTemplate = runPropertiesElement ? cloneXmlNode(runPropertiesElement) : null;
  const applyBaseRunProps = (runNode) => applyRunPropertiesTemplate(runNode, runPropsTemplate);
  const replaceRunProps = (runNode) => {
    const existingRunPropertyChanges = collectRunPropertyChanges(runNode);

    // Remove existing rPr if any
    if (Array.isArray(runNode.elements)) {
      runNode.elements = runNode.elements.filter((el) => el?.name !== 'w:rPr');
    } else {
      runNode.elements = [];
    }
    if (runPropsTemplate) {
      runNode.elements.unshift(cloneXmlNode(runPropsTemplate));
    }

    restoreRunPropertyChanges(runNode, existingRunPropertyChanges);

    if (!existingRunPropertyChanges.length && runTrackFormatMark) {
      const runPropertiesNode = getRunPropertiesNode(runNode);
      appendTrackFormatChangeToRunProperties(runPropertiesNode, [runTrackFormatMark]);
    }
  };

  const runs = [];

  childElements.forEach((child) => {
    if (!child) return;
    if (child.name === 'w:r') {
      const clonedRun = cloneXmlNode(child);
      replaceRunProps(clonedRun);
      if (hasXmlNodeNamed(clonedRun, 'w:footnoteReference')) {
        ensureReferenceRunFormatting(clonedRun, 'w:footnoteReference');
      } else if (hasXmlNodeNamed(clonedRun, 'w:endnoteReference')) {
        ensureReferenceRunFormatting(clonedRun, 'w:endnoteReference');
      }
      runs.push(clonedRun);
      return;
    }

    if (child.name === 'w:hyperlink') {
      const hyperlinkClone = cloneXmlNode(child);
      if (Array.isArray(hyperlinkClone.elements)) {
        hyperlinkClone.elements.forEach((run) => applyBaseRunProps(run));
      }
      runs.push(hyperlinkClone);
      return;
    }

    if (child.name === 'w:ins' || child.name === 'w:del') {
      const trackedClone = cloneXmlNode(child);
      if (Array.isArray(trackedClone.elements)) {
        trackedClone.elements.forEach((element) => {
          if (element?.name === 'w:r') replaceRunProps(element);
        });
      }
      runs.push(trackedClone);
      return;
    }

    if (child.name === 'w:commentRangeStart' || child.name === 'w:commentRangeEnd') {
      const commentRangeClone = cloneXmlNode(child);
      runs.push(commentRangeClone);
      return;
    }

    // Run-level SDTs are paragraph siblings in OOXML (not children of w:r).
    // Emit them directly so Word does not need to normalize invalid nesting.
    if (child.name === 'w:sdt') {
      const sdtClone = cloneXmlNode(child);
      runs.push(sdtClone);
      return;
    }

    const runWrapper = { name: XML_NODE_NAME, elements: [] };
    applyBaseRunProps(runWrapper);
    if (!Array.isArray(runWrapper.elements)) runWrapper.elements = [];
    if (child.name === 'w:footnoteReference' || child.name === 'w:endnoteReference') {
      ensureReferenceRunFormatting(runWrapper, child.name);
    }
    runWrapper.elements.push(cloneXmlNode(child));
    runs.push(runWrapper);
  });

  const trackedRuns = ensureTrackedWrapper(runs, trackingMarksByType);

  if (!trackedRuns.length) {
    const emptyRun = { name: XML_NODE_NAME, elements: [] };
    applyBaseRunProps(emptyRun);
    trackedRuns.push(emptyRun);
  }

  if (decodedAttrs && Object.keys(decodedAttrs).length) {
    trackedRuns.forEach((run) => {
      run.attributes = { ...(run.attributes || {}), ...decodedAttrs };
    });
  }

  if (trackedRuns.length === 1) {
    return trackedRuns[0];
  }

  return trackedRuns;
};

/** @type {import('@translator').NodeTranslatorConfig} */
export const config = {
  xmlName: XML_NODE_NAME,
  sdNodeOrKeyName: SD_KEY_NAME,
  type: NodeTranslator.translatorTypes.NODE,
  encode,
  decode,
  attributes: validXmlAttributes,
};

/** @type {import('@translator').NodeTranslator} */
export const translator = NodeTranslator.from(config);
